import { init as initMd4x, renderToHtml } from 'md4x/wasm';
import { useEffect } from 'react';
import { css } from 'styled-components';
import { tokens } from 'styles/tokens';
import { preprocessText } from './preprocessors';
import { blockquotesStyles } from './preprocessors/blockquote';
import { sanitizeHtml } from './sanitize';

let initializationPromise: Promise<void> | null = null;

async function init() {
    if (initializationPromise) {
        await initializationPromise;
        return;
    }

    initializationPromise = initMd4x();

    try {
        await initializationPromise;
    } catch (e) {
        initializationPromise = null;
        throw e;
    }
}

// Renders comment text (which may contain {{mention:UUID}} tokens and
// CommonMark markdown) to a sanitized HTML string.
export async function renderCommentMarkdown(text: string): Promise<string> {
    await init();
    const html = renderToHtml(preprocessText(text));
    return sanitizeHtml(html);
}

export function useRenderMarkdown(
    text: string,
    containerRef: React.RefObject<HTMLDivElement>,
    postRenderCallback?: () => void,
): void {
    useEffect(() => {
        const container = containerRef.current;
        if (!container) {
            return;
        }

        let active = true;
        renderCommentMarkdown(text).then((html) => {
            if (!active) {
                return;
            }
            container.innerHTML = html;
            if (postRenderCallback) {
                postRenderCallback();
            }
        });

        return () => {
            active = false;
        };
    }, [text, containerRef, postRenderCallback]);
}

export const markdownStyles = css`
    p {
        margin: 0.5em 0;
        &:first-child {
            margin-top: 0;
        }
        &:last-child {
            margin-bottom: 0;
        }
    }

    ul,
    ol {
        margin: 0.5em 0;
        padding-left: 1.5em;
    }

    code {
        font-family: monospace;
        background: rgba(0, 0, 0, 0.05);
        padding: 0.1em 0.3em;
        border-radius: 3px;
        font-size: 0.9em;
    }

    pre {
        background: rgba(0, 0, 0, 0.05);
        padding: 0.75em;
        border-radius: 4px;
        overflow-x: auto;
        margin: 0.5em 0;

        code {
            background: none;
            padding: 0;
        }
    }

    img {
        max-width: 100%;
        height: auto;
    }

    a {
        color: inherit;
        text-decoration: underline;
    }

    blockquote {
        margin: 0.5em 0;
        padding: 0.5em 1em;
        border-left: 4px solid ${tokens.border.default};
        background-color: ${tokens.comment.blockquoteBackground};
        border-radius: 3px;
    }

    ${blockquotesStyles}
`;
